import React, { useState, useEffect } from 'react';
import { 
  HardHat, ClipboardCheck, Building2, Factory, 
  TrendingUp, Clock, ShieldAlert, CheckCircle2,
  FileText, Zap, Users, Lock 
} from 'lucide-react';

const Solutions = ({ theme }) => {
  const [activeTab, setActiveTab] = useState(0);
  
  // --- ADDED MOBILE STATE ---
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // --------------------------

  const personas = [
    {
      role: "General Contractors",
      icon: <Building2 size={20} />,
      headline: "Protect your margins and your license.",
      description: "Stop chasing subcontractors for paperwork. Force compliance before they even step on site with our 'No-Permit, No-Log' automatic blocking.",
      impacts: [
        { label: "Liability Reduced", value: "100%", icon: <ShieldAlert size={18} /> },
        { label: "Admin Saved", value: "15 hrs/wk", icon: <Clock size={18} /> },
        { label: "Site Visibility", value: "Real-time", icon: <TrendingUp size={18} /> }
      ],
      features: [
        "Automated Insurance Expiry Alerts",
        "Live Manpower Count Dashboard",
        "Subcontractor Pre-qualification Portal"
      ],
      image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1000&q=80"
    },
    {
      role: "Site Owners & FM",
      icon: <Factory size={20} />,
      headline: "Total control over external contractors.",
      description: "Whether you run a manufacturing plant, university, or data center, external maintenance teams are your biggest risk. Ensure every visitor is induced, insured, and permitted.",
      impacts: [
        { label: "Unauthorized Access", value: "Zero", icon: <Lock size={18} /> },
        { label: "Contractor Compliance", value: "100%", icon: <CheckCircle2 size={18} /> },
        { label: "Asset Uptime", value: "Optimized", icon: <TrendingUp size={18} /> }
      ],
      features: [
        "Visitor & Contractor Kiosk Mode",
        "Permit-to-Work Gate Control",
        "Asset Maintenance History Logs"
      ],
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=1000&q=80"
    },
    {
      role: "Safety Officers",
      icon: <ClipboardCheck size={20} />,
      headline: "Audit trails that survive courtrooms.",
      description: "Every action is timestamped, geo-tagged, and digitally signed. When an incident happens, you have the data instantly.",
      impacts: [
        { label: "Audit Readiness", value: "Instant", icon: <FileText size={18} /> },
        { label: "Incident Rate", value: "-40%", icon: <TrendingUp size={18} /> },
        { label: "Paperwork", value: "Eliminated", icon: <ShieldAlert size={18} /> }
      ],
      features: [
        "One-Click ISO Audit Export",
        "Digital RAMS Sign-off Tracking",
        "Corrective Action (CAPA) Loop"
      ],
      image: "https://images.unsplash.com/photo-1581092335397-9583eb92d232?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
    },
    {
      role: "Subcontractors",
      icon: <HardHat size={20} />,
      headline: "Zero friction for your workforce.",
      description: "Your team doesn't need to download an app or remember a password. They scan, sign, and work. We make compliance the easiest part of their day.",
      impacts: [
        { label: "Onboarding Time", value: "< 2 mins", icon: <Zap size={18} /> },
        { label: "App Downloads", value: "Zero", icon: <Users size={18} /> },
        { label: "Payment Disputes", value: "Reduced", icon: <CheckCircle2 size={18} /> }
      ],
      features: [
        "QR Code 'Scan & Sign' Entry",
        "No-Login Permit Requests",
        "Auto-saved Training Records"
      ],
      image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1000&q=80"
    }
  ];

  return (
    <section id="solutions" style={{ padding: isMobile ? '60px 20px' : '100px 20px', backgroundColor: 'white' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '60px' }}>
          <h2 style={{ fontSize: isMobile ? '2rem' : '2.5rem', color: theme.secondary, marginBottom: '20px', fontWeight: '800' }}>
            Built for the entire <span style={{ color: theme.primary }}>site ecosystem</span>
          </h2>
        </div>

        {/* Tabs Navigation */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '40px', flexWrap: 'wrap' }}>
          {personas.map((p, i) => (
            <button
              key={i}
              onClick={() => setActiveTab(i)}
              style={{
                padding: isMobile ? '10px 16px' : '12px 24px', // Smaller buttons on mobile
                borderRadius: '30px',
                border: 'none',
                backgroundColor: activeTab === i ? theme.primary : '#f1f5f9',
                color: activeTab === i ? 'white' : '#64748b',
                fontWeight: '600',
                fontSize: isMobile ? '0.9rem' : '1rem', // Smaller text on mobile
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                boxShadow: activeTab === i ? '0 4px 12px rgba(37, 99, 235, 0.2)' : 'none',
                flexGrow: isMobile ? 1 : 0, // Allow buttons to stretch slightly on small screens
                justifyContent: 'center'
              }}
            >
              {p.icon} {p.role}
            </button>
          ))}
        </div>

        {/* Content Area - Grid fixed for mobile */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(450px, 1fr))', // Stack on mobile!
          gap: isMobile ? '30px' : '60px',
          alignItems: 'start',
          backgroundColor: '#f8fafc',
          padding: isMobile ? '20px' : '40px', // Reduce padding on mobile
          borderRadius: '24px',
          border: '1px solid #e2e8f0'
        }}>
          {/* Text Side */}
          <div style={{ textAlign: 'left' }}>
            <h3 style={{ fontSize: isMobile ? '1.5rem' : '2rem', color: theme.secondary, marginBottom: '15px', lineHeight: '1.2', fontWeight: 'bold' }}>
              {personas[activeTab].headline}
            </h3>
            <p style={{ fontSize: isMobile ? '1rem' : '1.1rem', color: '#475569', marginBottom: '30px', lineHeight: '1.6' }}>
              {personas[activeTab].description}
            </p>
            
            {/* Impact Grid - Fixed for mobile */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', // Stack vertically on mobile!
                gap: '15px', 
                marginBottom: '40px' 
            }}>
                {personas[activeTab].impacts.map((imp, idx) => (
                    <div key={idx} style={{ background: 'white', padding: '15px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', display: isMobile ? 'flex' : 'block', alignItems: 'center', gap: '15px' }}>
                        <div style={{ color: theme.primary, marginBottom: isMobile ? '0' : '8px' }}>{imp.icon}</div>
                        <div>
                          <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>{imp.label}</div>
                          <div style={{ fontSize: '1.1rem', color: theme.secondary, fontWeight: 'bold' }}>{imp.value}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Feature List */}
            <div>
                <h4 style={{ fontSize: '0.9rem', textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '1px', marginBottom: '15px' }}>Key Capabilities</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {personas[activeTab].features.map((feat, idx) => (
                        <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px', color: '#475569', fontWeight: '500', fontSize: isMobile ? '0.95rem' : '1rem' }}>
                            <CheckCircle2 size={18} color={theme.success} style={{ flexShrink: 0, marginTop: '2px' }} />
                            {feat}
                        </li>
                    ))}
                </ul>
            </div>
          </div>

          {/* Image Side - Fixed height for mobile */}
          <div style={{ 
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)', 
            borderRadius: '16px', 
            overflow: 'hidden',
            border: isMobile ? '4px solid white' : '8px solid white', // Thinner border on mobile
            height: isMobile ? '250px' : '500px', // Much shorter on mobile
            position: 'relative'
          }}>
            <img 
              src={personas[activeTab].image} 
              alt={personas[activeTab].role} 
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Solutions;